import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { API, formatApiErrorDetail } from "@/lib/site";

const INTERESTS = [
  "A2 Gir Cow Ghee",
  "Upcoming Products",
  "Wholesale",
  "Distribution",
  "International Enquiry",
];

const EMPTY = { name: "", phone: "", email: "", city: "", interest: INTERESTS[0], message: "" };

const inputCls =
  "mt-2 w-full border-b border-forest/20 bg-transparent py-3 text-base text-charcoal outline-none transition-colors duration-300 placeholder:text-charcoal/35 focus:border-gold";

export default function LeadCapture() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        email: form.email || null,
        city: form.city || null,
        message: form.message || null,
      });
      toast.success("Welcome to the MAHIRA family. We will reach out to you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="join" data-testid="lead-section" className="bg-cream py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-2 lg:gap-24">
        <div className="flex flex-col justify-center">
          <SectionHeading eyebrow="Stay Connected" title="Stay Connected With MAHIRA">
            Be the first to know about new batches, upcoming products and
            members-only updates. One short form — no spam, ever.
          </SectionHeading>
          <Reveal delay={0.2}>
            <ul className="mt-10 space-y-4 text-sm text-charcoal/65">
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                Early access to new MAHIRA products
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                Priority for wholesale and distribution enquiries
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                Direct line to the MAHIRA team
              </li>
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            data-testid="lead-form"
            onSubmit={submit}
            className="rounded-[28px] border border-forest/10 bg-white/70 p-8 shadow-[0_40px_90px_-40px_rgba(26,54,38,0.35)] backdrop-blur-md md:p-10"
          >
            <div className="grid gap-7 sm:grid-cols-2">
              <label className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-forest/60">
                Name *
                <input
                  data-testid="lead-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </label>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-forest/60">
                Mobile / WhatsApp *
                <input
                  data-testid="lead-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 …"
                  className={inputCls}
                />
              </label>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-forest/60">
                Email
                <input
                  data-testid="lead-email-input"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </label>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-forest/60">
                City
                <input
                  data-testid="lead-city-input"
                  value={form.city}
                  onChange={set("city")}
                  placeholder="Your city"
                  className={inputCls}
                />
              </label>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-forest/60 sm:col-span-2">
                I&apos;m interested in
                <select
                  data-testid="lead-interest-select"
                  value={form.interest}
                  onChange={set("interest")}
                  className={`${inputCls} cursor-pointer`}
                >
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-forest/60 sm:col-span-2">
                Message (optional)
                <textarea
                  data-testid="lead-message-input"
                  rows={2}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Anything you'd like us to know"
                  className={`${inputCls} resize-none`}
                />
              </label>
            </div>
            <button
              data-testid="lead-submit-button"
              type="submit"
              disabled={sending}
              className="mt-9 w-full rounded-full bg-forest py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest-light disabled:opacity-60"
            >
              {sending ? "Joining…" : "Join MAHIRA"}
            </button>
            <p className="mt-4 text-center text-xs text-charcoal/45">
              We respect your privacy. Your details are used only to respond to
              your enquiry.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
